<script setup lang="ts">

import {faTrashAlt} from "@fortawesome/pro-regular-svg-icons";
import {faPlusCircle} from "@fortawesome/pro-light-svg-icons";
import {RadioCard, RadioCardGroup} from "@/shared/components/ui/radio-group";
import EmptyCard from "@/shared/components/ui/radio-group/EmptyCard.vue";
import {HTMLAttributes, ref, watch} from "vue";
import {Address} from "@/shared/graphql/types";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {useI18n} from "vue-i18n";

type colRange = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

interface Props {
  addresses: Address[]
  columns: colRange
  colspan: number
  selected: string
  deleteAction?: boolean
  createAction?: boolean
  class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
  deleteAction: true,
  createAction: true
})
const emit = defineEmits<{
  (e: 'address:selected', address: Address): void
  (e: 'address:delete', address: Address): void
  (e: 'address:create'): void
}>()
const {t} = useI18n()
const selectedAddress = ref<string>(props.selected)

const selectAddress = (addressId: string) => {
  selectedAddress.value = addressId
  const address = props.addresses.find(a => a.id === addressId)
  emit('address:selected', address);
}

watch(() => props.selected, () => {
  selectedAddress.value = props.selected
})

const deleteAddressDialog = (address: Address) => emit('address:delete', address);
const createAddressDialog = async () => emit('address:create');

</script>

<template>
  <RadioCardGroup :default-value="selected" :col-span="colspan" :cols="columns" @update:model-value="selectAddress" :class="props.class">
    <RadioCard v-for="address in addresses" :value="address.id" :data-address="address.id" :key="address.id">
      <p class="text-left justify-items-start text-sm text-slate-500" v-html="address.formatted"/>
      <p v-if="address.isBillingAddress"
         class="absolute top-2 m-0 bg-gray-200 px-1.5 py-0 rounded text-[10px]"
         :class="selectedAddress == address.id ? 'right-[30px]' : 'right-2'"
      >{{
          t('')
        }}</p>
      <FontAwesomeIcon v-if="deleteAction && !address.isBillingAddress && selectedAddress != address.id"
                       :icon="faTrashAlt"
                       @click.stop="deleteAddressDialog(address)"
                       class="absolute top-2 right-2 m-0 py-0 rounded text-gray-400"/>
    </RadioCard>
    <EmptyCard v-if="createAction" @click="createAddressDialog" class="border-dashed hover:border-gray-500">
      <div class="flex flex-col text-gray-400 gap-1 items-center justify-center h-full" data-tests="new-delivery-address">
        <FontAwesomeIcon :icon="faPlusCircle" size="xl" class=""/>
        {{ t('shared.address_list.new_delivery_address') }}
      </div>
    </EmptyCard>
  </RadioCardGroup>
</template>

<style scoped>

</style>